import { forwardRef, useEffect, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import clsx from "clsx";
import { NativeSelect } from "@/components/ui/NativeSelect";
import {
  formatGermanMoneyInput,
  isGermanDecimalTyping,
  parseGermanDecimal,
} from "@/lib/germanDecimal";

interface BaseFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  fieldId?: string;
}

export function FieldRow({
  label,
  hint,
  error,
  fieldId,
  className,
  children,
}: BaseFieldProps & { children: ReactNode }) {
  return (
    <div className={clsx("space-y-1", className)}>
      {label && (
        <label className="eh-label" htmlFor={fieldId}>
          {label}
        </label>
      )}
      {children}
      {hint && !error && (
        <p className="text-xs text-label-secondary">{hint}</p>
      )}
      {error && <p className="text-xs text-status-red">{error}</p>}
    </div>
  );
}

export interface TextInputProps
  extends BaseFieldProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, "className"> {
  inputClassName?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, className, inputClassName, id, ...rest },
  ref,
) {
  return (
    <FieldRow label={label} hint={hint} error={error} fieldId={id} className={className}>
      <input ref={ref} id={id} className={clsx("eh-input", inputClassName)} {...rest} />
    </FieldRow>
  );
});

export interface GermanDecimalInputProps extends Omit<TextInputProps, "type" | "value" | "onChange"> {
  value: number | null | undefined;
  onValueChange: (value: number) => void;
  /** Wenn true, bleibt 0 als leeres Feld (optionaler Betrag). */
  emptyWhenZero?: boolean;
}

/** Geldeingabe mit Komma, ohne Verschlucken von Zwischenständen wie „123,“ oder „123,0“. */
export function GermanDecimalInput({
  value,
  onValueChange,
  emptyWhenZero = false,
  placeholder = "0,00",
  ...rest
}: GermanDecimalInputProps) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => {
    const numeric = Number(value);
    const show = Number.isFinite(numeric) && (!emptyWhenZero || numeric !== 0);
    return show ? formatGermanMoneyInput(numeric) : "";
  });

  useEffect(() => {
    if (focused) return;
    const next = Number(value);
    const show = Number.isFinite(next) && (!emptyWhenZero || next !== 0);
    setText(show ? formatGermanMoneyInput(next) : "");
  }, [emptyWhenZero, focused, value]);

  return (
    <TextInput
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      placeholder={placeholder}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        if (!isGermanDecimalTyping(raw)) return;
        setText(raw);
        if (raw.trim() === "") {
          onValueChange(0);
          return;
        }
        const parsed = parseGermanDecimal(raw);
        if (parsed != null) onValueChange(parsed);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseGermanDecimal(text);
        const next = parsed ?? 0;
        onValueChange(next);
        const show = Number.isFinite(next) && (!emptyWhenZero || next !== 0);
        setText(show ? formatGermanMoneyInput(next) : "");
      }}
    />
  );
}

export interface SelectInputProps
  extends BaseFieldProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, "className"> {
  inputClassName?: string;
  selectSize?: "sm" | "md";
}

export function SelectInput({
  label,
  hint,
  error,
  className,
  inputClassName,
  selectSize,
  id,
  children,
  ...rest
}: SelectInputProps) {
  return (
    <FieldRow label={label} hint={hint} error={error} fieldId={id} className={className}>
      <NativeSelect id={id} className={inputClassName} selectSize={selectSize} {...rest}>
        {children}
      </NativeSelect>
    </FieldRow>
  );
}

export interface TextareaProps
  extends BaseFieldProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  inputClassName?: string;
}

export function TextareaInput({
  label,
  hint,
  error,
  className,
  inputClassName,
  id,
  ...rest
}: TextareaProps) {
  return (
    <FieldRow label={label} hint={hint} error={error} fieldId={id} className={className}>
      <textarea id={id} className={clsx("eh-input", inputClassName)} rows={3} {...rest} />
    </FieldRow>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, id, className, ...rest }: CheckboxProps) {
  return (
    <label
      htmlFor={id}
      className={clsx("flex items-start gap-2 text-sm text-label", className)}
    >
      <input
        type="checkbox"
        id={id}
        className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
        {...rest}
      />
      <span>
        <span className="font-medium">{label}</span>
        {description && (
          <span className="block text-xs text-label-secondary">{description}</span>
        )}
      </span>
    </label>
  );
}
